import { useMemo } from "react";
import prettyBytes from "pretty-bytes";

import type { TierDistributionRowResponse } from "@/apiClient/models/TierDistributionRowResponse";
import { manifestLabel } from "@/components/folderConfig/folderConfig.utils";
import Badge from "@/components/storageOps/Badge";
import {
  TIER_BADGE_CLASSES,
  TIER_OPTIONS,
} from "@/components/storageOps/storageOps.utils";

type TierTotals = { links: number; bytes: number };

const sumRows = (rows: TierDistributionRowResponse[]): TierTotals =>
  rows.reduce(
    (totals, row) => ({
      links: totals.links + row.links,
      bytes: totals.bytes + row.bytes,
    }),
    { links: 0, bytes: 0 }
  );

const tierSortIndex = (tier: string): number => {
  const index = TIER_OPTIONS.indexOf(tier as (typeof TIER_OPTIONS)[number]);
  return index === -1 ? TIER_OPTIONS.length : index;
};

function TierDistributionPanel({
  rows,
  isLoading,
  groupByManifestType,
  onGroupByManifestTypeChange,
}: {
  rows: TierDistributionRowResponse[];
  isLoading: boolean;
  groupByManifestType: boolean;
  onGroupByManifestTypeChange: (groupByManifestType: boolean) => void;
}) {
  const groupedRows = useMemo(() => {
    if (groupByManifestType) {
      return [...rows].sort(
        (a, b) =>
          a.manifest_type.localeCompare(b.manifest_type) ||
          tierSortIndex(a.tier) - tierSortIndex(b.tier)
      );
    }

    const byTier = new Map<string, TierTotals>();
    for (const row of rows) {
      const totals = byTier.get(row.tier) ?? { links: 0, bytes: 0 };
      byTier.set(row.tier, {
        links: totals.links + row.links,
        bytes: totals.bytes + row.bytes,
      });
    }

    return [...byTier.entries()]
      .sort(([a], [b]) => tierSortIndex(a) - tierSortIndex(b))
      .map(([tier, totals]) => ({
        tier,
        manifest_type: "",
        links: totals.links,
        bytes: totals.bytes,
      }));
  }, [rows, groupByManifestType]);

  const totals = useMemo(() => sumRows(rows), [rows]);

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
        <h2 className="text-lg font-semibold text-blue-200">
          Tier Distribution
        </h2>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-blue-300">
          <input
            type="checkbox"
            className="form-checkbox rounded text-blue-100"
            checked={groupByManifestType}
            onChange={(event) =>
              onGroupByManifestTypeChange(event.target.checked)
            }
          />
          Group by manifest type
        </label>
      </div>
      {isLoading ? (
        <p className="px-5 py-4 text-sm text-grey-200">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="px-5 py-4 text-sm text-grey-200">
          No tiered links found.
        </p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-[11px] font-bold uppercase tracking-[0.08em] text-blue-100">
              <th className="px-5 py-2">Tier</th>
              {groupByManifestType && <th className="px-5 py-2">Manifest Type</th>}
              <th className="px-5 py-2 text-right">Links</th>
              <th className="px-5 py-2 text-right">Size</th>
            </tr>
          </thead>
          <tbody>
            {groupedRows.map((row) => (
              <tr
                key={`${row.tier}-${row.manifest_type}`}
                className="border-b border-slate-50 text-blue-300"
              >
                <td className="px-5 py-2">
                  <Badge
                    label={row.tier}
                    colorClasses={TIER_BADGE_CLASSES[row.tier]}
                  />
                </td>
                {groupByManifestType && (
                  <td className="px-5 py-2">
                    {manifestLabel(row.manifest_type)}
                  </td>
                )}
                <td className="px-5 py-2 text-right">
                  {row.links.toLocaleString()}
                </td>
                <td className="px-5 py-2 text-right">
                  {prettyBytes(row.bytes)}
                </td>
              </tr>
            ))}
            <tr className="font-semibold text-blue-200">
              <td className="px-5 py-2">Total</td>
              {groupByManifestType && <td className="px-5 py-2" />}
              <td className="px-5 py-2 text-right">
                {totals.links.toLocaleString()}
              </td>
              <td className="px-5 py-2 text-right">
                {prettyBytes(totals.bytes)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
}

export default TierDistributionPanel;
